import { useEffect, useState } from "react";
import { useEnduserStore } from "../store/hooks";
import { useZendeskIntegration } from "../../shared/store/portalSchemaStore";

const ZENDESK_JWT_FETCH_TIMEOUT_MS = 4000;

/**
 * Fetches a Zendesk Messaging auth JWT from the business's own customer-hosted signing
 * endpoint (config.jwtEndpoint), passing only the enduser's Tellescope authToken. The
 * endpoint resolves identity server-side and signs the JWT with the business's Zendesk key.
 *
 * Non-blocking: returns null on timeout/error/disabled-config, since Messaging has no
 * fallback identify path today — the widget simply stays unauthenticated on failure.
 */
export const useZendeskAuth = (widgetVersion: "classic" | "messaging" | undefined) => {
  const config = useZendeskIntegration();
  const authToken = useEnduserStore((state) => state.session?.authToken);
  const [fetchedJwt, setFetchedJwt] = useState<string | null>(null);

  const shouldFetch = widgetVersion === "messaging" && !!config?.authEnabled && !!config?.jwtEndpoint && !!authToken;
  const jwtEndpoint = config?.jwtEndpoint;

  useEffect(() => {
    if (!shouldFetch || !jwtEndpoint || !authToken) {
      return;
    }

    if (!jwtEndpoint.startsWith("https://")) {
      console.error("useZendeskAuth: refusing to send authToken to a non-HTTPS jwtEndpoint");
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ZENDESK_JWT_FETCH_TIMEOUT_MS);

    fetch(jwtEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ authToken }),
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Zendesk JWT endpoint returned ${res.status}`);
        return res.json();
      })
      .then((data: unknown) => {
        if (typeof data === "object" && data !== null && "jwt" in data && typeof data.jwt === "string") {
          setFetchedJwt(data.jwt);
        } else {
          throw new Error("Zendesk JWT endpoint returned an unexpected response shape");
        }
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error("useZendeskAuth: failed to fetch Zendesk auth JWT", err);
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [shouldFetch, jwtEndpoint, authToken]);

  return shouldFetch ? fetchedJwt : null;
};
